import {
  ComponentPropsWithoutRef,
  KeyboardEvent,
  MouseEvent,
  forwardRef,
  useRef,
  useEffect,
} from "react";
import styles from "./SideNav.module.scss";
import { sideNavKeyboardNavigation } from "./utils";
import { useSideNav } from "./SideNav";

/**
 * Props for the SideNavLink component
 * @extends Omit<ComponentPropsWithoutRef<"a">, "id" | "onClick" | "onKeyDown" | "onKeyUp">
 */
export type SideNavLinkProps = Omit<
  ComponentPropsWithoutRef<"a">,
  "id" | "onClick" | "onKeyDown" | "onKeyUp"
> & {
  /**
   * Unique identifier for the navigation link
   */
  id: string;
  /**
   * When true, marks the link as the current active page
   */
  active?: boolean;
  /**
   * When true, disables the link and prevents interaction
   */
  disabled?: boolean;
  /**
   * Function called when the link is clicked
   */
  onClick?: (
    e: MouseEvent<HTMLAnchorElement>,
    id: SideNavLinkProps["id"],
  ) => void;
  /**
   * Function called when a key is pressed down on the link
   */
  onKeyDown?: (
    e: KeyboardEvent<HTMLAnchorElement>,
    id: SideNavLinkProps["id"],
  ) => void;
  /**
   * Function called when a key is released on the link
   */
  onKeyUp?: (
    e: KeyboardEvent<HTMLAnchorElement>,
    id: SideNavLinkProps["id"],
  ) => void;
};

/**
 * SideNavLink component for individual navigation links within a SideNav.
 *
 * Features:
 * - Individual navigation links with active state support
 * - Keyboard navigation integration with arrow keys
 * - Focus management and tabindex handling
 * - Accessible with proper ARIA attributes
 * - Click and keyboard event handling
 * - Disabled state support
 * - Automatic active state tracking through context
 *
 * @example
 * <SideNav.Link id="intro" href="/intro" active>
 *   Introduction
 * </SideNav.Link>
 */
export const SideNavLink = forwardRef<HTMLLIElement, SideNavLinkProps>(
  (props, ref) => {
    const {
      className,
      children,
      href,
      id,
      active,
      onClick,
      onKeyDown,
      onKeyUp,
      style,
      disabled,
      ...rest
    } = props;
    const { links } = useSideNav();

    const linkRef = useRef<HTMLAnchorElement>(null);
    const { activeIds, addActiveId, removeActiveId } = useSideNav();

    useEffect(() => {
      if (!activeIds.has(id) && active) {
        addActiveId(id);
        return;
      }
      if (activeIds.has(id) && !active) {
        removeActiveId(id);
      }
    }, [active, activeIds]);

    function onKeyDownHandler(e: KeyboardEvent<HTMLAnchorElement>) {
      sideNavKeyboardNavigation(e, links, linkRef);
      onKeyDown?.(e, id);
    }

    function onKeyUpHandler(e: KeyboardEvent<HTMLAnchorElement>) {
      if (disabled) return;
      if (e.code === "Space" || e.code === "Enter") {
        e.preventDefault();
        if (onClick) {
          e.currentTarget.click();
        }
      }

      onKeyUp?.(e, id);
    }

    function onClickHandler(e: MouseEvent<HTMLAnchorElement>) {
      if (disabled) {
        e.preventDefault();
        return;
      }

      // find current item that has tabindex 0
      const current = Array.prototype.slice
        .call(links)
        .filter((child) => (child as HTMLElement).tabIndex === 0)[0];

      // remove above from tabbable
      if (current) current.setAttribute("tabindex", "-1");

      // make label button tabbable
      linkRef.current?.setAttribute("tabindex", "0");
      linkRef.current?.focus();

      onClick?.(e, id);
    }

    return (
      <li className={className} data-anv="sidenav-link" style={style} ref={ref}>
        <a
          className={styles["sidenav-link"]}
          aria-current={active ? "page" : undefined}
          href={href}
          ref={linkRef}
          onClick={onClickHandler}
          onKeyDown={onKeyDownHandler}
          onKeyUp={onKeyUpHandler}
          id={id}
          aria-disabled={disabled}
          tabIndex={active ? 0 : -1}
          {...rest}
        >
          {children}
        </a>
      </li>
    );
  },
);

SideNavLink.displayName = "SideNavLink";
